import React, { useCallback, useMemo, useState } from 'react'
import { CircularProgress, makeStyles } from '@material-ui/core'
import { Button, Modal, PageTitle } from '../root/components/common'
import { useDispatch, useSelector } from 'react-redux'
import {
    getDevicesByIds,
    massDeleteDevices
} from '../root/hooks/use-devices-multiselect/devices-multiselect-api'
import { enqueueSnackbar } from '../root/actions'
import { useQuery } from 'react-query'
import { DELETE_DEVICES_MODAL_STEPS, TRANSFER_MODE } from '../root/constants'
import { OperationModeIcon } from '../root/components/common/operation-mode-icon'
import { TableVirtualized } from '../root/components/common/table-virtualized/table'
import { Trash2, CheckIcon } from 'lucide-react'
import { UserPermissions } from '../../services/permissions'

/* eslint-disable */
const useStyles = makeStyles(theme => ({
    root: {
        maxHeight: '90vh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
    },
    pageTitle: {
        marginBottom: 40
    },
    btnsWrapper: {
        marginTop: 20,
        display: 'flex',
        justifyContent: 'flex-end'
    }
}))

export const DeleteDevicesModal = ({
    onClose,
    open,
    selectedDevicesIds,
    excludedDevicesIds,
    filters,
    clearSelection,
    reloadDevices
}) => {
    const dispatch = useDispatch()
    const classes = useStyles()

    const locale = useSelector(state => state.main.locale)
    const theme = useSelector(state => state.main.theme)
    const p = useSelector(state => state.main.p)

    const [isNotAssignedStepApproved, setIsNotAssignedStepApproved] =
        useState(false)
    const [isAssignedStepApproved, setIsAssignedStepApproved] = useState(false)

    const { data: devicesToDelete, isLoading: isLoadingDevices } = useQuery(
        [
            'loadDevicesToDelete',
            selectedDevicesIds,
            excludedDevicesIds,
            filters,
            open
        ],
        () => getDevicesByIds(selectedDevicesIds, excludedDevicesIds, filters),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
            enabled: open,
            onError: () => {
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.getDevicesEx,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        }
    )

    const { devicesDeletable, devicesNotDeletable } = useMemo(() => {
        return {
            devicesDeletable: devicesToDelete?.filter(
                d =>
                    UserPermissions.deviceV2(d.is_assigned, d.customer_id)
                        .delete
            ),
            devicesNotDeletable: devicesToDelete?.filter(
                d =>
                    !UserPermissions.deviceV2(d.is_assigned, d.customer_id)
                        .delete
            )
        }
    }, [devicesToDelete])

    const currentStep = useMemo(() => {
        if (devicesToDelete?.length > 0) {
            if (devicesNotDeletable.length > 0 && !isNotAssignedStepApproved) {
                return DELETE_DEVICES_MODAL_STEPS.not_assigned_step
            } else if (
                (devicesNotDeletable.length === 0 ||
                    isNotAssignedStepApproved) &&
                !isAssignedStepApproved
            ) {
                return DELETE_DEVICES_MODAL_STEPS.assigned_step
            } else if (
                (devicesNotDeletable.length === 0 ||
                    isNotAssignedStepApproved) &&
                isAssignedStepApproved
            ) {
                return DELETE_DEVICES_MODAL_STEPS.confirmation
            }
        }

        return null
    }, [
        devicesToDelete,
        devicesDeletable,
        devicesNotDeletable,
        isNotAssignedStepApproved,
        isAssignedStepApproved
    ])

    const handleConfirm = useCallback(() => {
        if (currentStep === DELETE_DEVICES_MODAL_STEPS.not_assigned_step) {
            setIsNotAssignedStepApproved(true)
        } else if (currentStep === DELETE_DEVICES_MODAL_STEPS.assigned_step) {
            if (devicesDeletable?.length > 0) {
                setIsAssignedStepApproved(true)
            }
        } else if (currentStep === DELETE_DEVICES_MODAL_STEPS.confirmation) {
            const deleteBody = {
                devices: devicesDeletable.map(d => ({
                    id: d.id,
                    location_id: d.location_id,
                    customer_id: d.customer_id
                }))
            }
            massDeleteDevices(deleteBody)
                .then(deleteResult => {
                    dispatch(
                        enqueueSnackbar({
                            message: locale.devices.devicesRemovedFromOrg,
                            options: {
                                variant: 'success'
                            }
                        })
                    )
                })
                .catch(() => {
                    dispatch(
                        enqueueSnackbar({
                            message: locale.sagasMsgs.commonLoadingError,
                            options: {
                                variant: 'error'
                            }
                        })
                    )
                })
                .finally(() => {
                    cleanUp()
                    onClose()
                    reloadDevices(filters)
                })
        }
    }, [
        currentStep,
        devicesDeletable,
        filters,
        cleanUp,
        onClose,
        reloadDevices
    ])

    const handleCancel = useCallback(() => {
        cleanUp(true)
        onClose()
    }, [cleanUp, onClose])

    const cleanUp = useCallback(
        (isCancel = false) => {
            setIsAssignedStepApproved(false)
            setIsNotAssignedStepApproved(false)
            if (!isCancel) {
                clearSelection()
            }
        },
        [
            setIsAssignedStepApproved,
            setIsNotAssignedStepApproved,
            clearSelection
        ]
    )

    const devicesColumns = [
        {
            key: 'mode',
            label: '',
            sortable: false,
            dataType: 'string',
            width: 0.5,
            maxWidth: 60,
            minWidth: 60,
            alignCenter: true,
            render: ({ rowData, cellData }) => {
                const isAssigned = rowData.is_assigned
                if (rowData.hasOwnProperty('transfer_mode')) {
                    return (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}>
                            <OperationModeIcon
                                operationMode={rowData.operation_mode}
                                locale={locale}
                                size="small"
                            />
                            <div
                                style={{
                                    fontSize: 12
                                }}>
                                {rowData.transfer_mode === TRANSFER_MODE.wifi
                                    ? 'WiFi'
                                    : null}
                                {rowData.transfer_mode === TRANSFER_MODE.lora
                                    ? 'LoRa'
                                    : null}
                                {rowData.transfer_mode ===
                                TRANSFER_MODE.cellular
                                    ? locale.devices.cellular
                                    : null}
                            </div>
                        </div>
                    )
                } else {
                    return (
                        <OperationModeIcon
                            operationMode={rowData.operation_mode}
                            locale={locale}
                        />
                    )
                }
            }
        },
        {
            key: 'name',
            label: locale.common.name,
            sortable: true,
            dataType: 'string',
            width: 1.5,
            maxWidth: 100,
            render: ({ rowData }) => {
                return rowData.name
            }
        },
        {
            key: 'serial_number',
            label: locale.locations.deviceSerial,
            sortable: true,
            dataType: 'string',
            width: 1,
            maxWidth: 100,
            render: ({ rowData }) => rowData.serial_number
        },
        {
            key: 'location_name',
            label: locale.devices.location,
            sortable: true,
            dataType: 'string',
            width: 1.5,
            maxWidth: 140,
            render: ({ rowData, cellData }) => rowData.location_name
        },
        {
            key: 'customer_name',
            label: '',
            sortable: false,
            dataType: 'string',
            // alignCenter: true,
            width: 1,
            maxWidth: 90,
            render: ({ rowData }) => {
                return rowData.customer_name
            }
        },
        {
            key: 'role_name',
            label: '',
            sortable: false,
            dataType: 'string',
            // alignCenter: true,
            width: 1,
            maxWidth: 90,
            render: ({ rowData }) => {
                return p[rowData.customer_id]?.role_name || ''
            }
        }
    ]

    const notDeletableColumns = [
        ...devicesColumns,
        {
            key: 'is_assigned',
            label: '',
            sortable: false,
            dataType: 'string',
            // alignCenter: true,
            width: 1,
            maxWidth: 80,
            render: ({ rowData }) => {
                return rowData.is_assigned
                    ? locale.common.assigned
                    : locale.devices.notAssigned
            }
        }
    ]

    const assignedColumns = [
        ...devicesColumns,
        {
            key: 'action_access',
            label: '',
            sortable: false,
            dataType: 'string',
            // alignCenter: true,
            width: 1,
            maxWidth: 80,
            render: ({ rowData }) => {
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Trash2 size={22} color={theme.primary} />
                        <CheckIcon size={22} color={theme.primary} />
                    </div>
                )
            }
        }
    ]

    const isConfirmButtonDisabled = useMemo(() => {
        if (
            currentStep === DELETE_DEVICES_MODAL_STEPS.assigned_step &&
            devicesDeletable.length === 0
        ) {
            return true
        }
        return false
    }, [currentStep, devicesDeletable])

    return (
        <Modal
            size="lg"
            onClose={handleCancel}
            open={open}
            className={classes.root}>
            <PageTitle
                title={locale.devices.delete}
                className={classes.pageTitle}
            />
            {isLoadingDevices ? (
                <div style={{ height: '50vh' }}>
                    <div
                        style={{
                            display: 'flex',
                            height: '100%',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                        <CircularProgress size={20} color={theme.primary} />
                    </div>
                </div>
            ) : null}
            {!isLoadingDevices && currentStep ? (
                <>
                    {currentStep ===
                    DELETE_DEVICES_MODAL_STEPS.not_assigned_step ? (
                        <>
                            <div>
                                <span style={{ fontSize: 20 }}>ⓘ</span>
                                {
                                    locale.devices
                                        .youAreNotAbleToDeleteTheseDevices
                                }
                            </div>
                            <div style={{ marginTop: 8 }}>
                                {locale.devices.devicesAmount}:{' '}
                                {devicesNotDeletable.length}
                            </div>
                            <div style={{ height: 300, marginTop: 12 }}>
                                <TableVirtualized
                                    data={devicesNotDeletable}
                                    columns={notDeletableColumns}
                                    disableHeader={true}
                                />
                            </div>
                        </>
                    ) : null}
                    {currentStep ===
                    DELETE_DEVICES_MODAL_STEPS.assigned_step ? (
                        <>
                            {devicesDeletable.length === 0 ? (
                                <div>
                                    {locale.devices.noDevicesYouCanDelete}
                                </div>
                            ) : null}
                            {devicesDeletable.length > 0 ? (
                                <>
                                    <div>
                                        <span style={{ fontSize: 20 }}>ⓘ</span>{' '}
                                        {locale.devices.devicesReadyToDelete}
                                    </div>
                                    <div style={{ marginTop: 8 }}>
                                        {locale.devices.devicesAmount}:{' '}
                                        {devicesDeletable.length}
                                    </div>
                                    <div style={{ height: 300, marginTop: 12 }}>
                                        <TableVirtualized
                                            data={devicesDeletable}
                                            columns={assignedColumns}
                                            disableHeader={true}
                                        />
                                    </div>
                                </>
                            ) : null}
                        </>
                    ) : null}
                    {currentStep === DELETE_DEVICES_MODAL_STEPS.confirmation ? (
                        <div>
                            {locale.devices.areYouSureYouWantToDeleteDevices}
                        </div>
                    ) : null}
                </>
            ) : null}
            <div className={classes.btnsWrapper}>
                <Button
                    style={{ marginRight: 8 }}
                    onClick={handleConfirm}
                    disabled={isConfirmButtonDisabled}>
                    {locale.common.confirm}
                </Button>
                <Button reverted onClick={handleCancel}>
                    {locale.common.cancel}
                </Button>
            </div>
        </Modal>
    )
}
/* eslint-enable */
